/**
 * LandingPage
 *
 * The public page at `/`. It is the only surface an anonymous visitor sees
 * before `/login`, so it fetches nothing, spends no AI tokens and renders
 * outside `AppShell` — the shell loads projects and would bounce a visitor
 * with no session. Every claim here describes something that ships today:
 * the walkthrough ends on "approve → backlog", and the MCP bullet speaks for
 * the MCP server rather than the unbuilt editor handoff (FLOW-529).
 */

import { Component, For, onMount } from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { getMe } from "../api/auth";
import { GitHubIcon, PlanDemo, PricingTable } from "../components/landing";
import { logoSrc } from "../stores/themeStore";

const FEATURES: { title: string; body: string }[] = [
  {
    title: "Grounded breakdowns",
    body: "Every epic and story is anchored in the repository you linked, the documents you wrote and the backlog you already have.",
  },
  {
    title: "Decision memory",
    body: "Flowicity records the decisions behind the work and flags a new item that contradicts one.",
  },
  {
    title: "Your backlog in Claude Code",
    body: "Flowicity's MCP server hands Claude Code your backlog, documents and decisions.",
  },
];

const navLinkClass =
  "rounded text-sm text-ink-2 hover:text-ink hit-area focus-ring";

export const LandingPage: Component = () => {
  const navigate = useNavigate();

  // A returning user who bookmarked `/` wants the app, not the marketing.
  // The page renders regardless: a public first paint must never wait on an
  // API call, and a rejection here is the normal anonymous case.
  onMount(() => {
    getMe()
      .then((me) =>
        navigate(me.status === "active" ? "/board" : "/pending", {
          replace: true,
        }),
      )
      .catch(() => {});
  });

  return (
    <div class="min-h-screen bg-paper">
      <header class="border-b border-line">
        <div class="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <a href="#top" class="rounded hit-area focus-ring">
            <img src={logoSrc()} alt="Flowicity" class="w-7 h-7 rounded" />
          </a>
          <nav aria-label="Landing page" class="flex items-center gap-4">
            <a href="#demo" class={navLinkClass}>
              See it work
            </a>
            <a href="#pricing" class={navLinkClass}>
              Pricing
            </a>
            <A href="/login" class={navLinkClass}>
              Sign in
            </A>
          </nav>
        </div>
      </header>

      <main
        id="top"
        class="max-w-5xl mx-auto px-4 py-12 sm:py-16 flex flex-col gap-16"
      >
        <section class="flex flex-col items-start gap-6">
          <h1 class="text-3xl sm:text-4xl">
            Describe it. Get a grounded plan.
          </h1>
          <p class="max-w-2xl text-base sm:text-lg text-ink-2">
            Flowicity turns a plain-language initiative into an epic and stories
            grounded in your repo, your docs and your past decisions — with
            estimates you can commit to.
          </p>
          <A
            href="/login"
            class="inline-flex items-center justify-center gap-3 px-5 py-3 bg-ink text-paper rounded font-medium hover:bg-ink-2 transition-colors focus-ring"
          >
            <span aria-hidden="true" class="inline-flex">
              <GitHubIcon class="w-5 h-5" />
            </span>
            Sign up with GitHub
          </A>
        </section>

        <section id="demo" aria-labelledby="demo-heading">
          <h2 id="demo-heading" class="mb-6">
            Watch Plan turn a sentence into a backlog
          </h2>
          <PlanDemo />
        </section>

        <section aria-labelledby="features-heading">
          <h2 id="features-heading" class="mb-6">
            Grounded, not generated
          </h2>
          <ul class="grid gap-4 sm:grid-cols-3">
            <For each={FEATURES}>
              {(feature) => (
                <li class="flex flex-col gap-2 rounded border border-line bg-surface p-5">
                  <h3 class="font-medium text-ink">{feature.title}</h3>
                  <p class="text-sm text-ink-2">{feature.body}</p>
                </li>
              )}
            </For>
          </ul>
        </section>

        <PricingTable />
      </main>

      <footer class="border-t border-line">
        <div class="max-w-5xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-4">
          <p class="text-sm text-ink-3">© 2026 Flowicity</p>
          <nav aria-label="Legal" class="flex items-center gap-4">
            <A href="/terms" class={navLinkClass}>
              Terms
            </A>
            <A href="/privacy" class={navLinkClass}>
              Privacy
            </A>
          </nav>
        </div>
      </footer>
    </div>
  );
};
